<template>
  <div class="relative flex w-auto flex-1 lg:w-[220px] xl:w-[300px]">
    <div id="autocomplete"></div>
  </div>
</template>

<script setup lang="ts">
import { autocomplete } from "@algolia/autocomplete-js";
import { createAlgoliaInsightsPlugin } from "@algolia/autocomplete-plugin-algolia-insights";
import { createRedirectUrlPlugin } from "@algolia/autocomplete-plugin-redirect-url";
import aa from "search-insights";
import { onMounted, onUnmounted, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import "@algolia/autocomplete-theme-classic/dist/theme.css";
import { useRoute, useRouter } from "vue-router";
import { useLanguages } from "@/core/composables";
import { globals } from "@/core/globals";
import { Logger } from "@/core/utilities";
import { useThorlabsGtm } from "@/shared/gtm/gtm";
import { isCoBrowsingActive } from "@/shared/live-person/composables/useLivePerson";
import { getIndexName } from "@/shared/thorlabs/search";
import { eventBus } from "@/shared/thorlabs/shared/event-bus";
import { getFamilyPagesPlugin } from "./plugins/familyPagesPlugin";
import { getGuidesPlugin } from "./plugins/guidesPlugin";
import { getProductsPlugin } from "./plugins/productsPlugin";
import { createQuerySuggestionsPluginInstance } from "./plugins/querySuggestionPlugin";
import { createRecentSearchesPluginInstance } from "./plugins/recentSearchesPlugin";
import { searchClient } from "./plugins/searchClient";
import { getSoftwarePagesPlugin } from "./plugins/softwarePagesPlugin";
import { getTechnicalResourcesPlugin } from "./plugins/technicalResourcesPlugin";
import { useCoBrowseSearch } from "./useCoBrowseSearch";
import type { IAlgoliaProduct /*, ITlAlgoliaItemClickedEventData*/ } from "./searchTypes";
import type { BaseItem } from "@algolia/autocomplete-core";
import type { AutocompleteCollection } from "@algolia/autocomplete-js";
import type { Ref } from "vue";

// Define the Html type since it's not exported from @algolia/autocomplete-js
const emit = defineEmits(["search-submitted", "item-selected"]);
let ac: ReturnType<typeof autocomplete> | undefined = undefined;
const { t } = useI18n();
const { gtmSendSearchEvent, gtmSendAutocompleteSearchEvent } = useThorlabsGtm();
const { currentLocale } = useLanguages();
// Flag to suppress the panel after search submission
let suppressPanel = false;
// Flag to track whether we programmatically focused the search input (so we can undo it on navigation)
let didAutoFocusSearch = false;
let searchInitTimeoutId: number | undefined;
// Whether the auto-focus-on-homepage-first-load has already been attempted
let hasHandledHomeAutoFocus = false;
const shouldAutoFocusOnHomeLoad = () => !hasHandledHomeAutoFocus;
const markHomeAutoFocusHandled = () => {
  hasHandledHomeAutoFocus = true;
};
const isHomePagePath = (path: string) => path === "/" || /^\/[a-z]{2}\/?$/.test(path);

// Reads a redirect URL already captured by the redirect-url plugin from Algolia's autocomplete context.
const getContextRedirectUrl = (context: unknown): string | undefined => {
  const data = (context as { redirectUrlPlugin?: { data?: Array<{ urls?: string[] }> } })?.redirectUrlPlugin?.data;
  return data?.find((redirect) => (redirect.urls?.length ?? 0) > 0)?.urls?.[0];
};

const resolveAlgoliaRedirectUrl = async (query: string): Promise<string | undefined> => {
  try {
    const indexName = getIndexName("products", currentLocale.value);
    const { results } = await searchClient.value.search({
      requests: [{ indexName, query, hitsPerPage: 0, enableRules: true }],
    });
    const firstResult = results?.[0] as { renderingContent?: { redirect?: { url?: string } } } | undefined;
    return firstResult?.renderingContent?.redirect?.url ?? undefined;
  } catch (error) {
    Logger.error("algolia-autocomplete: failed to resolve redirect URL", error);
    return undefined;
  }
};

const route = useRoute();
const router = useRouter();

// LivePerson co-browse search handling (TL-3774) — see useCoBrowseSearch.ts
const coBrowseSearch = useCoBrowseSearch({
  getAutocomplete: () => ac,
  setSuppressPanel: (suppress) => {
    suppressPanel = suppress;
  },
  onSearchCorrected: (query) => {
    emit("search-submitted");
    gtmSendSearchEvent(query);
  },
});

// When the search input was auto-focused on homepage, undo it on the first route change.
watch(
  () => route.path,
  () => {
    if (!didAutoFocusSearch) {
      return;
    }
    didAutoFocusSearch = false;

    const inputEl = document.querySelector(".aa-Input") as HTMLInputElement | null;
    if (!inputEl) {
      return;
    }

    // Clear the caret so the blinking cursor disappears even if the browser retains it visually after blur
    inputEl.setSelectionRange(0, 0);
    inputEl.blur();

    // Reset the browser's sequential focus navigation starting point to the top of the DOM.
    // Without this, pressing Tab after blur would resume from the search input's position
    // instead of starting from the first focusable element.
    const appEl = document.getElementById("app");
    if (appEl) {
      appEl.setAttribute("tabindex", "-1");
      appEl.focus({ preventScroll: true });
      appEl.removeAttribute("tabindex");
    }
  },
);

aa("init", {
  appId: globals.algoliaConfig.applicationId,
  apiKey: globals.algoliaConfig.searchApiKey,
});

onUnmounted(() => {
  ac?.destroy();
  if (searchInitTimeoutId) {
    window.clearTimeout(searchInitTimeoutId);
  }
  // Remove the event listeners when component is unmounted
  eventBus.off("closeSearch");
  document.removeEventListener("closeSearchDropdown", handleCloseSearchDropdown);
  // Remove injected mobile header style
  const mobileHeaderStyle = document.getElementById("mobile-header-panel-style");
  if (mobileHeaderStyle) {
    mobileHeaderStyle.remove();
  }
});

// Prevent any native submit on the Algolia search form (e.g. a LivePerson co-browse
// replayed Enter) from triggering a full page reload. Search navigation is handled by
// onSubmit via router.push.
const preventSearchFormNativeSubmit = (e: Event) => {
  const target = e.target as HTMLElement | null;
  if (target?.closest("#autocomplete .aa-Form")) {
    e.preventDefault();
  }
};

// Handler for the custom DOM event
const handleCloseSearchDropdown = () => {
  // Set flag to suppress panel reopening after route change
  suppressPanel = true;

  // Close the autocomplete
  if (ac) {
    ac.setIsOpen(false);
  }

  // Remove the panel
  const panel = document.querySelector(".aa-Panel");
  if (panel) {
    panel.remove();
  }

  // Blur the input to prevent Algolia from reopening the panel
  const inputEl = document.querySelector(".aa-Input") as HTMLInputElement | null;
  if (inputEl) {
    inputEl.blur();
  }

  emit("item-selected");
  eventBus.emit("closeSearch");
};

onMounted(() => {
  const navigateToRedirectUrl = (
    url: string,
    modifiers?: { metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean },
  ) => {
    let target: URL;
    try {
      target = new URL(url, window.location.origin);
    } catch (error) {
      Logger.error("algolia-autocomplete: invalid redirect URL", error);
      return;
    }
    // Block javascript:/data: and any other non-http(s) scheme Algolia could return.
    if (target.protocol !== "http:" && target.protocol !== "https:") {
      Logger.error("algolia-autocomplete: blocked non-http(s) redirect URL", url);
      return;
    }
    if (modifiers?.metaKey || modifiers?.ctrlKey || modifiers?.shiftKey) {
      window.open(target.toString(), "_blank", "noopener,noreferrer");
      return;
    }
    if (target.origin === window.location.origin) {
      void router.push(target.pathname + target.search + target.hash);
      return;
    }
    window.location.assign(target.toString());
  };

  const redirectUrlPlugin = createRedirectUrlPlugin({
    onRedirect(redirects, { event }) {
      const url = redirects?.[0]?.urls?.[0];
      if (url) {
        navigateToRedirectUrl(url, event as { metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean });
      }
    },
  });

  // Measure actual scrollbar width and expose it as a CSS variable so that
  // the sticky header can compensate when overflow:hidden hides the scrollbar.
  // window.innerWidth includes the scrollbar; clientWidth excludes it.
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.documentElement.style.setProperty("--scrollbar-width", `${scrollbarWidth}px`);

  // Add listener for custom DOM event
  document.addEventListener("closeSearchDropdown", handleCloseSearchDropdown);

  // In LivePerson co-browsing sessions the representative's replayed Enter key can trigger a
  // native submit on the Algolia search form that bypasses Algolia's own preventDefault,
  // causing a full page reload with an empty query (overwriting the correct router navigation).
  // Block any native submit on the search form; navigation is handled by onSubmit via router.push.
  document.addEventListener("submit", preventSearchFormNativeSubmit, true);

  // Add listener for input changes to handle the initial-load class
  document.addEventListener("input", handleInputChange);

  //These 2 variables are used to track when to send autocomplete GTM event
  const previousAutocompleteStatus: Ref<string> = ref("idle"); //Algolia autocomplete states can be 'idle', 'loading' or 'stalled'
  const previousSentAutocompleteTerm: Ref<string | undefined> = ref(undefined);
  // Last query for which the autocomplete sources actually finished resolving. Used on submit to
  // decide whether an empty redirect context is authoritative (no redirect) or just not resolved yet.
  let lastResolvedQuery: string | undefined;

  ac = autocomplete({
    container: "#autocomplete",
    plugins: [
      createQuerySuggestionsPluginInstance(),
      // @ts-expect-error - It is a valid type, but it has debounce on it
      getProductsPlugin(),
      // @ts-expect-error - It is a valid type, but it has debounce on it
      getGuidesPlugin(),
      // @ts-expect-error - It is a valid type, but it has debounce on it
      getFamilyPagesPlugin(),
      // @ts-expect-error - It is a valid type, but it has debounce on it
      getSoftwarePagesPlugin(),
      // @ts-expect-error - It is a valid type, but it has debounce on it
      getTechnicalResourcesPlugin(),
      createRecentSearchesPluginInstance(),
      redirectUrlPlugin,
      createAlgoliaInsightsPlugin({ insightsClient: aa }),
    ],
    insights: true,
    openOnFocus: false,
    stallThreshold: 400,
    detachedMediaQuery: "none",
    placeholder: "Search",
    // Don't use renderSubmitButton as Algolia might be overriding it
    // We'll use CSS to hide the default icon and show our custom one
    render({ elements, render, html, state }, root) {
      const {
        querySuggestionsPlugin,
        recentSearchesPlugin,
        softwarePages,
        technicalResources,
        products,
        productsAdditional,
        guides,
        familyPages,
      } = elements;

      const recentSearchCollection = state.collections.find(
        (collection) => collection.source.sourceId === "recentSearchesPlugin",
      );
      const productItemsCollection = state.collections.find((collection) => collection.source.sourceId === "products");
      const hideOtherSources =
        productItemsCollection?.items.some((item) => (item as unknown as IAlgoliaProduct).hideOtherSources === true) ??
        false;
      const searchQuery = state.query.trim();
      const noMatchesMessage = searchQuery
        ? `${t("autocomplete.search.no_matches")} for "${searchQuery}"`
        : t("autocomplete.search.no_matches");

      const template = html`<div>
        <div class="aa-PanelLayout aa-Panel--scrollable">
          <div class="aa-PanelSections">
            <div class="aa-PanelSection--left">
              <div class="aa-PanelSectionSources">
                <h6>${t("pages.search.recent_searches")}</h6>
                ${recentSearchCollection
                  ? recentSearchesPlugin
                  : html`<div class="aa-SourceNoResults">${noMatchesMessage}</div>`}
              </div>
              <div class="aa-PanelSectionSources">
                <h6>${t("pages.search.suggestions")}</h6>
                ${querySuggestionsPlugin}
              </div>
              ${hideOtherSources || isEmptySource(state.collections, "technicalResources")
                ? ""
                : html` <div class="aa-PanelSection--tech-resources">
                    <div class="aa-PanelSectionSource">${technicalResources}</div>
                  </div>`}
              ${hideOtherSources || isEmptySource(state.collections, "softwarePages")
                ? ""
                : html` <div class="aa-PanelSection--soft-dwd">
                    <div class="aa-PanelSectionSource">${softwarePages}</div>
                  </div>`}
            </div>
            <div class="aa-PanelSection--right">
              ${hideOtherSources || isEmptySource(state.collections, "familyPages")
                ? ""
                : html` <div class="aa-PanelSection--fp">
                    <div class="aa-PanelSectionSource">${familyPages}</div>
                  </div>`}
              ${isEmptySource(state.collections, "products")
                ? ""
                : html` <div class="aa-PanelSection--products">
                    <div class="aa-PanelSectionSource">${products}</div>
                    ${isEmptySource(state.collections, "productsAdditional")
                      ? ""
                      : html`<div class="aa-PanelSectionSource aa-PanelSectionSource--additional">
                          ${productsAdditional}
                        </div>`}
                  </div>`}
              ${hideOtherSources || isEmptySource(state.collections, "guides")
                ? ""
                : html` <div class="aa-PanelSection--guides">
                    <div class="aa-PanelSectionSource">${guides}</div>
                  </div>`}
            </div>
          </div>
        </div>
      </div>`;

      // @ts-expect-error - It is assignable to VNodeChild
      render(template, root);
    },
    onSubmit({ state, event }) {
      event.preventDefault();
      // Co-browse: agent keystrokes bypass Algolia's state, so trust the DOM value first;
      // otherwise state.query stays the source of truth.
      const inputEl = document.querySelector(".aa-Input") as HTMLInputElement | null;
      const domValue = inputEl?.value?.trim() || "";
      const searchQuery = isCoBrowsingActive.value
        ? coBrowseSearch.resolveSubmitQuery(domValue, state.query || "")
        : state.query || domValue || "";
      // Co-browse: a replayed Enter can fire with an empty input — skip, re-check for a late term.
      if (isCoBrowsingActive.value && !searchQuery) {
        coBrowseSearch.scheduleSubmitRecheck("");
        return;
      }
      // Co-browse: input cleared and all fallbacks resolve to the already-searched term —
      // skip the stale re-navigation and let the re-check pick up the real term.
      if (coBrowseSearch.isStaleReplayedSubmit(domValue, searchQuery)) {
        coBrowseSearch.scheduleSubmitRecheck(searchQuery);
        return;
      }
      // Set flag to suppress panel reopening after route change
      suppressPanel = true;
      // Co-browse: re-assert the query so replay can't blank the box or spawn an empty resubmit.
      if (isCoBrowsingActive.value) {
        coBrowseSearch.recordSubmittedQuery(searchQuery);
        ac?.setQuery(searchQuery);
        if (inputEl && inputEl.value !== searchQuery) {
          inputEl.value = searchQuery;
        }
        coBrowseSearch.scheduleSubmitRecheck(searchQuery);
      }
      // Emit an event to notify the parent component
      emit("search-submitted");
      // Explicitly close the autocomplete dropdown and remove the panel
      ac?.setIsOpen(false);
      const panel = document.querySelector(".aa-Panel");
      if (panel) {
        panel.remove();
      }
      // Blur the input to prevent Algolia from reopening the panel
      if (inputEl) {
        inputEl.blur();
      }

      gtmSendSearchEvent(searchQuery);

      // Honor Algolia redirect query rules (e.g. "contact" → Locations) on Enter exactly like the
      // magnifier does. If the redirect-url plugin already captured the redirect, let it navigate.
      if (getContextRedirectUrl(state.context)) {
        return;
      }
      // If the sources already resolved for this exact term, the empty redirect context is
      // authoritative — there is no redirect, so go straight to search without an extra Algolia
      // request. During co-browse the tracked state is unreliable, so always fall through.
      if (!isCoBrowsingActive.value && lastResolvedQuery === searchQuery) {
        void router.push({ name: "Search", query: { q: searchQuery } });
        return;
      }
      // A fast Enter beat the debounced products source that carries the redirect rules, so resolve
      // the redirect directly before falling back to the search results page.
      void (async () => {
        const redirectUrl = searchQuery ? await resolveAlgoliaRedirectUrl(searchQuery) : undefined;
        if (redirectUrl) {
          navigateToRedirectUrl(redirectUrl);
          return;
        }
        void router.push({ name: "Search", query: { q: searchQuery } });
      })();
    },
    onStateChange({ state }) {
      // Keep the co-browse drift-detection mirror in sync with Algolia's actual query state.
      coBrowseSearch.updateAlgoliaQueryMirror(state.query || "");
      // If the status changed from loading to idle and we have a autocomplete query, we can send autocomplete GTM event
      if (state.status === "idle" && previousAutocompleteStatus.value !== "idle") {
        lastResolvedQuery = state.query;
        if (state.query && state.query !== previousSentAutocompleteTerm.value) {
          gtmSendAutocompleteSearchEvent(state.query);
          previousSentAutocompleteTerm.value = state.query;
        }
      }

      previousAutocompleteStatus.value = state.status;
    },
  });
  // Method to set the top CSS value for the aa-Panel element based on .header height and optional .alert-banner sibling before .header
  const setPanelTopToHeaderHeight = () => {
    if (window.innerWidth >= 1024) {
      return;
    }
    const header = document.querySelector("[aria-label='header']") as HTMLElement | null;
    let topValue = 0;
    if (header) {
      const isSticky = header.classList.contains("sticky");
      let alertBannerHeight = 0;

      if (!isSticky) {
        // Check for .alert-banner sibling before .header (position="Top")
        // Note: Banners with position="Bottom" are inside the header and already included in header.offsetHeight
        let prev = header.previousElementSibling as HTMLElement | null;
        while (prev) {
          if (prev.classList.contains("alert-banner")) {
            alertBannerHeight = prev.offsetHeight;
            break;
          }
          prev = prev.previousElementSibling as HTMLElement | null;
        }
      }

      topValue = header.offsetHeight + alertBannerHeight + 64;
    }
    const panel = document.querySelector(".aa-Panel") as HTMLElement | null;
    if (panel && topValue > 0) {
      // Create or update inline style with !important for mobile header positioning
      let mobileHeaderStyle = document.getElementById("mobile-header-panel-style");
      if (!mobileHeaderStyle) {
        mobileHeaderStyle = document.createElement("style");
        mobileHeaderStyle.id = "mobile-header-panel-style";
        document.head.appendChild(mobileHeaderStyle);
      }
      // Calculate max-height: viewport height - top position - bottom padding
      const maxHeight = `calc(100dvh - ${topValue + 2}px - 40px)`;
      mobileHeaderStyle.textContent = `
        @media (max-width: 1023px) {
          .aa-Panel {
            top: ${topValue + 2}px !important;
          }
          .aa-Panel--scrollable {
            max-height: ${maxHeight} !important;
          }
        }
      `;
    }
  };

  // Add the custom search icon via direct DOM manipulation to ensure it overrides Algolia's default
  searchInitTimeoutId = window.setTimeout(() => {
    const submitButton = document.querySelector(".aa-SubmitButton");
    if (submitButton) {
      const svgIcon = document.querySelector(".aa-SubmitButton svg");
      if (svgIcon) {
        svgIcon.outerHTML = `
          <svg class="aa-SubmitIcon custom-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.7422 10.3439C12.5329 9.2673 13 7.9382 13 6.5C13 2.91015 10.0899 0 6.5 0C2.91015 0 0 2.91015 0 6.5C0 10.0899 2.91015 13 6.5 13C7.93858 13 9.26801 12.5327 10.3448 11.7415L10.3439 11.7422C10.3734 11.7822 10.4062 11.8204 10.4424 11.8566L14.2929 15.7071C14.6834 16.0976 15.3166 16.0976 15.7071 15.7071C16.0976 15.3166 16.0976 14.6834 15.7071 14.2929L11.8566 10.4424C11.8204 10.4062 11.7822 10.3734 11.7422 10.3439ZM12 6.5C12 9.53757 9.53757 12 6.5 12C3.46243 12 1 9.53757 1 6.5C1 3.46243 3.46243 1 6.5 1C9.53757 1 12 3.46243 12 6.5Z" fill="currentColor"/>
          </svg>
        `;
      }
    }
    setPanelTopToHeaderHeight();

    // Auto-focus the search input when the component is mounted,
    // but only on first load and only on homepage.
    const inputElement = document.querySelector(".aa-Input") as HTMLInputElement | null;
    if (inputElement) {
      // Always add the click listener
      inputElement.addEventListener("click", handleSearchInputClick);

      // Only auto-focus on first load and on homepage
      if (shouldAutoFocusOnHomeLoad()) {
        const activeEl = document.activeElement;
        const isUserInForm =
          activeEl instanceof HTMLInputElement ||
          activeEl instanceof HTMLTextAreaElement ||
          activeEl instanceof HTMLSelectElement;
        const currentPath = router.currentRoute.value.path;
        const isHomePage = isHomePagePath(currentPath);

        if (!isUserInForm && isHomePage) {
          inputElement.focus();
          didAutoFocusSearch = true;
        }

        markHomeAutoFocusHandled();
      }
    }
  }, 100); // Set up a MutationObserver to watch for the panel appearing/disappearing

  // Method to remove/hide/destroy the aa-Panel if #app does NOT have mobile-menu-open
  const removePanelIfNoMobileMenu = () => {
    const appEl = document.getElementById("app");
    const panel = document.querySelector(".aa-Panel");
    if (panel && appEl && !appEl.classList.contains("mobile-menu-open")) {
      // Hide the panel
      panel.remove();
      // Optionally, also close autocomplete if open
      ac?.setIsOpen(false);
    }
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "childList") {
        // Check if an .aa-Panel was added or removed
        const panel = document.querySelector(".aa-Panel");
        if (panel) {
          // If panel was suppressed (e.g. after search submit), remove it immediately
          if (suppressPanel) {
            ac?.setIsOpen(false);
            panel.remove();
            if (window.innerWidth >= 1024) {
              document.body.style.overflow = "";
            }
            return;
          }

          // When panel becomes visible, lock scrolling (desktop only - mobile-header manages this)
          if (window.innerWidth >= 1024) {
            document.body.style.overflow = "hidden";
          }

          // Add "initial-load" class if there's no query
          // We get the current input value directly from the DOM to ensure it's up-to-date
          const inputElement = document.querySelector(".aa-Input") as HTMLInputElement;
          if (inputElement && !inputElement.value.trim()) {
            panel.classList.add("initial-load");
          } else if (inputElement && inputElement.value.trim()) {
            panel.classList.remove("initial-load");
          }

          // Prevent touchmove events on mobile when panel is open
          document.addEventListener("touchmove", preventTouchMove, { passive: false });
          setPanelTopToHeaderHeight();
        } else {
          // When panel is removed, allow scrolling again (desktop only - mobile-header manages this)
          if (window.innerWidth >= 1024) {
            document.body.style.overflow = "";
          }
          document.removeEventListener("touchmove", preventTouchMove);

          // Don't re-focus — the user clicked away intentionally.
          suppressPanel = true;
        }
      }
    }
  });

  //Prevent touch moves on mobile to disable scrolling on body, but allow scrolling within the panel
  const preventTouchMove = (e: TouchEvent) => {
    const panel = document.querySelector(".aa-Panel");
    if (panel) {
      // Check if the touch event is inside the panel
      const target = e.target as HTMLElement;
      if (!panel.contains(target)) {
        // Only prevent if touch is outside the panel
        e.preventDefault();
      }
    }
  };
  // Setup a listener for the input value changes
  //Start observing the DOM for .aa-Panel changes
  observer.observe(document.body, { childList: true, subtree: true });

  // Listen for closeSearch event
  eventBus.on("closeSearch", () => {
    if (ac) {
      ac.setIsOpen(false);
    }
    removePanelIfNoMobileMenu();
  });

  // Optionally, listen for changes to #app class (e.g., mobile menu toggling)
  const appEl = document.getElementById("app");
  let lastMobileMenuState = appEl?.classList.contains("mobile-menu-open");
  const appClassObserver = new MutationObserver(() => {
    const currentState = appEl?.classList.contains("mobile-menu-open");
    if (currentState !== lastMobileMenuState) {
      lastMobileMenuState = currentState;
      removePanelIfNoMobileMenu();
    }
  });
  if (appEl) {
    appClassObserver.observe(appEl, { attributes: true, attributeFilter: ["class"] });
  }

  // Clean up the MutationObserver when component is unmounted
  onBeforeUnmount(() => {
    observer.disconnect();
    appClassObserver.disconnect();
    document.removeEventListener("touchmove", preventTouchMove as EventListener);
    document.removeEventListener("input", handleInputChange);
    document.removeEventListener("submit", preventSearchFormNativeSubmit, true);
    const inputElement = document.querySelector(".aa-Input") as HTMLInputElement | null;
    if (inputElement) {
      inputElement.removeEventListener("click", handleSearchInputClick);
    }
  });
});

// Open the panel when the user clicks the search input
const handleSearchInputClick = () => {
  suppressPanel = false;
  ac?.setIsOpen(true);
};

// Handler for input changes
const handleInputChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  if (target.classList.contains("aa-Input")) {
    // User is actively typing — allow the panel to open
    suppressPanel = false;
    // Keep the co-browse fallback term fresh without waiting for the next poll tick
    coBrowseSearch.noteTypedValue(target.value);
    const panel = document.querySelector(".aa-Panel");
    if (panel) {
      if (!target.value.trim()) {
        panel.classList.add("initial-load");
      } else {
        panel.classList.remove("initial-load");
      }
    }
  }
};

function isEmptySource(collections: AutocompleteCollection<BaseItem>[], name: string): boolean {
  const collection = collections.find((col) => col.source.sourceId === name);
  // eslint-disable-next-line sonarjs/prefer-single-boolean-return
  if (!collection || !collection?.items || collection.items.length === 0) {
    return true;
  }

  return false;
}
</script>

<style lang="scss">
#autocomplete {
  --aa-muted-color-rgb: --color-neutral-a3;
  --aa-primary-color-rgb: --color-neutral-a3;
  --aa-muted-color-alpha: 0.3;

  @apply w-full;
  .aa-Form {
    @apply flex items-center bg-[color:var(--color-neutral-a6)] border border-[color:var(--color-neutral-a3)] text-[color:var(--color-neutral-a1)] rounded-[4px] focus-within:border-black focus-within:outline-none focus-within:ring-0;
  }
  .aa-InputWrapper {
    @apply flex-grow pl-4 order-1;
  }
  .aa-InputWrapperSuffix {
    @apply order-2;
    button {
      @apply relative after:absolute after:content-[''] after:w-[1px] after:h-[15px] after:bg-[--color-neutral-a3] after:top-[50%] after:-translate-y-1/2 after:right-0 after:opacity-100 hover:text-[--color-primary-a1];
      &[hidden] {
        @apply after:opacity-0;
      }
    }
  }
  .aa-InputWrapperPrefix {
    order: 3;
    button {
      @apply hover:text-[--color-primary-a1];
    }
  }

  // Custom styles for the search button and icon
  .aa-SubmitButton {
    @apply flex items-center justify-center;

    // Hide default icon if our custom one is present
    svg.aa-SubmitIcon:not(.custom-search-icon) {
      @apply hidden;
    }

    .custom-search-icon {
      width: 16px !important;
      height: 16px !important;
      @apply block;
    }
  }
}

.aa {
  &-Panel {
    box-shadow: 0px 8px 16px 0px rgba(35, 38, 59, 0.25);

    @apply z-[40] mt-4 lg:mt-[20px] rounded-none lg:rounded-b-md pt-5 lg:pt-6 px-4 lg:px-3 py-[26px] w-full lg:w-auto;
    @apply lg:left-auto lg:max-w-[76dvw] #{!important};

    @media (max-width: theme("screens.md-max")) {
      @apply left-0 right-0 #{!important};
      position: fixed !important;
    }

    &--scrollable {
      @apply lg:max-h-[80vh] lg:px-2;
    }
  }
  &-Source {
    @apply static;
  }
  &-PanelLayout {
    @apply lg:max-h-[80vh];
  }
  &-PanelSections {
    @apply grid lg:grid-flow-col;

    @media (min-width: theme("screens.lg")) {
      gap: 30px;
    }

    .aa-PanelSection--left {
      @apply lg:w-[16.2rem];

      @media screen and (min-width: theme("screens.lg")) {
        > div:not(:last-child) {
          @apply border-b border-b-[--color-neutral-a4] pb-4 mb-5;
        }
      }
      @media screen and (max-width: theme("screens.md-max")) {
        > div {
          @apply border-b border-b-[--color-neutral-a4] pb-4 mb-5;
        }
      }
    }
    .aa-PanelSection--right {
      @apply relative flex flex-col lg:gap-5 lg:max-w-[710px] empty:hidden;

      .aa-List {
        @apply flex flex-col gap-5 lg:gap-4 pb-0;
      }

      @media screen and (max-width: theme("screens.md-max")) {
        @apply gap-5;
        > div[class*="aa-PanelSection"]:not(:last-child) {
          @apply border-b border-b-[--color-neutral-a4] pb-4;
        }
      }
      &:not(:has(div[class*="aa-PanelSection"] + div[class*="aa-PanelSection"])) {
        .aa-List {
          .aa-Item {
            &:last-child {
              @apply border-0;
            }
          }
        }
      }
    }
  }
  &-PanelSectionSources {
    h6 {
      @apply text-[--color-neutral-a2] text-base font-medium mt-0 mb-1 uppercase tracking-[1px];
    }
  }
  &-SourceHeader {
    @apply m-0 static;
    h6 {
      @apply gap-1 flex text-[--color-neutral-a2] text-base items-center font-medium m-0 uppercase tracking-[1px];
      svg {
        @apply inline-flex size-4;
      }
    }
    .search-bar__header {
      @apply flex justify-between gap-5 items-center;
      > a {
        @apply text-[--color-link] text-base font-medium hover:text-[--color-link-hover];
      }
    }
  }
  &-List {
    @apply pt-4 flex flex-col gap-4 pb-2;
  }
  &-Item {
    @apply p-0 block min-h-0;
    .aa-ItemContent {
      @apply flex items-start;
      &--link {
        @apply text-[--color-link] hover:text-[--color-link-hover];
      }
      svg {
        @apply text-[--color-neutral-a2] size-4 mt-0.5;
      }
    }
    &[aria-selected="true"] {
      @apply bg-transparent;
    }
  }
  &-PanelSection--fp {
    order: 2;
    .aa-Item {
      @apply p-0;
      .family-match {
        &--item {
          @apply flex gap-5 items-center w-full lg:border-b lg:border-[--color-neutral-a4] lg:pb-4;
        }
        &--image {
          @apply hidden lg:flex items-center justify-center w-[184px] h-[112px] border border-[--color-neutral-a4] rounded-md overflow-hidden shrink-0;
          box-shadow: 0px 4px 10px 0px rgba(223, 223, 223, 0.25);
          img {
            @apply max-w-full max-h-full h-auto rounded-md overflow-hidden;
          }
        }
        &--content {
          @apply flex flex-col gap-1;
          &-wrapper {
            @apply flex xl:flex-nowrap flex-wrap w-full gap-5 xl:items-center;
          }
        }
        &--name {
          @apply text-base font-bold;
        }
        &--info {
          @apply text-sm font-medium text-[--color-neutral-a2];
        }
        &--link {
          @apply hidden lg:block font-semibold px-6 py-3.5 border border-[--color-neutral-a3] rounded-md text-[--color-neutral-a1] hover:border-[--color-neutral-a2] whitespace-nowrap ml-auto;
        }
        &--title {
          @apply text-[--color-neutral-a2] text-base font-medium;
        }
      }
      &:last-child {
        .family-match--item {
          @apply lg:pb-[30px];
        }
      }
    }
  }
  &-PanelSection--products {
    order: 3;
    .aa-Item {
      @apply p-0;
      .product-match {
        &--item {
          @apply flex gap-3 lg:gap-5 items-center w-full lg:border lg:border-[--color-neutral-a4] lg:px-5 lg:py-4 lg:rounded-md;
        }
        &--image {
          @apply flex items-center justify-center w-[80px] h-[80px] shrink-0 lg:border-0 lg:rounded-none border border-[--color-neutral-a4] rounded-md overflow-hidden;

          img {
            @apply max-w-full max-h-full h-auto object-contain;
          }
        }
        &--content {
          @apply flex flex-col gap-2;
        }
        &--name {
          @apply text-base font-bold flex items-center gap-2.5;
        }
        &--info {
          @apply font-medium;
        }
        &--link {
          @apply text-[--color-link] hover:text-[--color-link-hover] font-medium flex items-center gap-1.5;
          svg {
            @apply size-4 fill-[--color-link] hover:fill-[--color-link-hover];
          }
        }
        &--addToCart {
          @apply border-[--color-neutral-a1] rounded-md text-[--color-neutral-a1] px-5 py-2.5 flex items-center gap-1.5 ml-auto hover:border-black hover:text-black;
          @apply border border-solid #{!important};
          svg {
            @apply size-5;
          }
        }
        &--additional-title {
          @apply text-[--color-neutral-a2] text-base font-medium mt-2 mb-1 uppercase tracking-[1px];
        }
      }
      @media screen and (min-width: theme("screens.lg")) {
        &:last-child {
          @apply border-b border-[--color-neutral-a4];
          .product-match--item {
            @apply mb-[30px];
          }
        }
      }
    }
    .exact-match {
      @apply border border-[--color-neutral-a4] rounded-full overflow-hidden text-xs font-medium text-[--color-neutral-a2] px-2.5 py-[3px] flex items-center gap-1;
    }

    .aa-PanelSectionSource {
      .discontinued-group {
        @apply pb-[30px];
      }
      @media screen and (max-width: theme("screens.md-max")) {
        &:has(+ .aa-PanelSectionSource--additional) {
          @apply border-b border-[--color-neutral-a4];
        }
      }
    }
  }
  &-PanelSection--guides {
    order: 4;
    .aa-List {
      @apply grid gap-0 lg:gap-4 pt-2 lg:pt-2.5 #{!important};
      @apply lg:grid-cols-[repeat(3,minmax(0,226px))] lg:gap-[30px];
      row-gap: 0 !important;
    }
    .aa-Item {
      @media screen and (min-width: theme("screens.lg")) {
        @apply items-stretch grid rounded-md border  border-[--color-neutral-a4] overflow-hidden gap-0;
        grid-row: span 2;
        grid-template-rows: subgrid;
        box-shadow: 0px 4px 10px 0px rgba(223, 223, 223, 0.25);
        img {
          @apply rounded-b-md;
        }
      }
    }
    .guide-match {
      &--item {
        @apply grid justify-between;
      }
      &--content {
        @apply py-3 lg:p-3 lg:text-13 font-medium lg:font-semibold;
        @apply leading-[1.25] #{!important};
      }
      &--image {
        @apply hidden lg:flex aspect-[1.6] max-h-[140px] w-full border-t-[1px] border-t-[--color-neutral-a4] mt-auto overflow-hidden;
        img {
          @apply max-w-full max-h-full h-auto;
        }
      }
    }
  }
  &-PanelSection--soft-dwd {
    .aa-SourceHeader {
      h6 {
        svg {
          @apply inline-flex size-5;
        }
      }
    }
  }
  &-SourceNoResults {
    @apply px-0;
  }
}

/* custom scss programming */
.aa-PanelSection--products:has(.exact-match) {
  order: 1;
}

.aa-PanelSectionSource--additional {
  @apply mt-4 pb-4 lg:pb-0;
  .product-match--info {
    @apply lg:text-base text-sm;
  }
  .aa-Item {
    @media screen and (min-width: theme("screens.lg")) {
      &:last-child {
        @apply border-0;
        .product-match--item {
          @apply mb-0;
        }
      }
    }
  }
}

.discontinued {
  &-item {
    @media screen and (min-width: theme("screens.lg")) {
      @apply flex flex-col gap-1.5 max-w-[347px] border border-[--color-neutral-a4] p-4 rounded-md;
    }
    @media screen and (max-width: theme("screens.md-max")) {
      @apply flex flex-row-reverse gap-3 items-center;
    }
    &--wrapper {
      @apply flex flex-col gap-1.5;
    }
    &--status {
      @apply text-[--color-neutral-a1] text-base font-semibold border-l-[2px] border-l-[--color-primary-a1] pl-2.5 mb-4;
      > a {
        @apply ml-1 text-[--color-link] hover:text-[--color-link-hover];
      }
    }
    &--info {
      @apply flex gap-2 items-center flex-wrap;
    }
    &--name {
      @apply text-base font-bold;
    }
    &--badge {
      @apply inline-flex bg-[--color-primary-a1] text-white font-bold text-xs px-2.5 py-1.5 rounded-full;
    }
    &--details {
      @apply text-sm lg:text-base font-medium;
    }
    &--image {
      @apply flex items-center justify-center w-[80px] h-[80px] aspect-square lg:aspect-auto lg:w-[268px] lg:h-[120px] mt-auto border border-[--color-neutral-a4] rounded-md overflow-hidden lg:border-none lg:rounded-none shrink-0 lg:self-center;
      img {
        @apply max-w-full max-h-full h-auto object-contain;
      }
    }
  }
  &-group--wrapper {
    @apply flex gap-4 flex-col lg:flex-row items-start lg:items-stretch;
  }
}
</style>
